import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import {
	PRISM_DIALOG_DATA,
	PrismButtonComponent,
	PrismDialogComponent,
	PrismDialogRef,
	PrismInputComponent,
	PrismProgressIndicatorComponent,
	TextValueAccessor
} from '@shure/prism-angular-components';
import {
	AuthenticationResultCode,
	DeviceAuthenticationCoreApiService
} from '@shure/shared/angular/data-access/device-authentication';
import { AuthenticationDevice } from '@shure/shared/angular/features/device-authentication/data-access';
import { DeviceImageComponent } from '@shure/shared/angular/ui/components/device-image';
import { CloseTextOption, SnackbarService } from '@shure/shared/angular/ui/components/html-snackbar';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

export interface SingleDeviceAuthData {
	device: AuthenticationDevice;
	showOptionReadOnly?: boolean;
	submitLabel?: string;
	preventCancelOnAuthPrompt?: boolean;
}

export enum SingleDeviceAuthResult {
	Cancelled,
	Readonly,
	Authenticated
}

const authenticationFailed = (): ValidatorFn => () => <ValidationErrors>{ authFailed: true };

@Component({
	selector: 'sh-single-device-auth',
	templateUrl: './single-device-auth.component.html',
	styleUrls: ['./single-device-auth.component.scss'],
	imports: [
		CommonModule,
		DeviceImageComponent,
		FlexLayoutModule,
		TranslocoModule,
		PrismButtonComponent,
		ReactiveFormsModule,
		PrismProgressIndicatorComponent,
		PrismInputComponent,
		PrismDialogComponent,
		TextValueAccessor
	],
	providers: [provideInlineScope('scDeviceAuth', (lang) => import(`../../../../i18n/${lang}`))]
})
export class SingleDeviceAuthComponent {
	protected singleDeviceAuthResult = SingleDeviceAuthResult;
	protected device: AuthenticationDevice = this.data.device;
	protected enableReadOnlyOption = this.data.showOptionReadOnly;
	protected preventCancelOnAuthPrompt = this.data.preventCancelOnAuthPrompt;
	protected submitLabel = this.data.submitLabel;
	protected authenticationFailed = false;
	protected isAuthenticating = false;
	constructor(
		private dialogRef: PrismDialogRef<SingleDeviceAuthComponent>,
		private readonly deviceAuthenticationService: DeviceAuthenticationCoreApiService,
		private readonly snackbarService: SnackbarService,
		private readonly transloco: TranslocoService,
		@Inject(PRISM_DIALOG_DATA) private readonly data: SingleDeviceAuthData
	) {}

	protected invalidPassword: ValidatorFn = () =>
		this.passwordInput?.pristine && this.authenticationFailed ? <ValidationErrors>{ invalidPassword: true } : null;
	protected passwordInput = new FormControl('', [Validators.required, this.invalidPassword]);

	protected async onUnlockClick(): Promise<void> {
		if (this.isAuthenticating || this.passwordInput.hasError('required')) {
			return;
		}
		this.passwordInput.disable();
		this.isAuthenticating = true;
		const successful = await this.tryAuthenticate();
		if (successful) {
			this.dialogRef.close(SingleDeviceAuthResult.Authenticated);
		} else {
			this.passwordInput.markAsPristine();
			this.passwordInput.enable();
		}
		this.isAuthenticating = false;
	}

	protected onReadOnlyClick(): void {
		if (this.isAuthenticating) {
			return;
		}
		this.deviceAuthenticationService.selectReadOnlyMode(this.device.id);
		this.dialogRef.close(SingleDeviceAuthResult.Readonly);
		this.snackbarService.open(
			this.transloco.translate('scDeviceAuth.tab.snackbar.locked-device-opened-readonly'),
			CloseTextOption.Ok
		);
	}

	protected onCancelClick(): void {
		this.dialogRef.close(SingleDeviceAuthResult.Cancelled);
	}

	private async tryAuthenticate(): Promise<boolean> {
		try {
			const res = await firstValueFrom(
				this.deviceAuthenticationService.authenticate([
					{
						deviceId: this.device.id,
						password: <string>this.passwordInput.value
					}
				])
			);
			if (!(res.response === AuthenticationResultCode.Ok)) {
				// TODO: Check for real reason, why authentication failed and update text in ui to match
				this.authenticationFailed = true;
				return false;
			}
			return true;
		} catch {
			// FIXME: Not sure we need a try/catch, but keeping is for now
			this.passwordInput.addValidators(authenticationFailed());
			return false;
		}
	}
}
